import styled from '@emotion/styled';
import _includes from 'lodash/includes';

import DeleteIcon from '@zola/zola-ui/src/components/SvgIcons/DeleteIcon';
import EditIcon from '@zola/zola-ui/src/components/SvgIcons/EditIcon';
import ReorderIcon from '@zola/zola-ui/src/components/SvgIcons/Reorder';
import EditPhotoUploadcare from 'components/common/EditPhotoUploadcare';

import FONT from '@zola/zola-ui/src/styles/emotion/fonts';
import SPACING from '@zola/zola-ui/src/styles/emotion/spacing';
import COLORS, {
  COLORS_BACKGROUND,
  COLORS_LINK,
  COLORS_TEXT,
} from '@zola/zola-ui/src/styles/emotion/colors';
import zIndex from '@zola/zola-ui/src/styles/emotion/zIndex';
import MEDIA_QUERY from '@zola/zola-ui/src/styles/emotion/mediaQuery';

import VideoSvg from 'assets/images/website/manage-redesign/video-upload-placeholder.svg';
import { css } from '@emotion/react';

const isPhotoOrVideo = (entityEditRemoveName?: string) =>
  _includes(['video', 'photo'], entityEditRemoveName);

export const SortableListContainer = styled.ul`
  padding-left: 0;
`;

export const StyledReorderIcon = styled(ReorderIcon)`
  flex: 0 0 24px;
  margin-right: ${SPACING.SM};
  &:active {
    cursor: grabbing;
  }

  ${MEDIA_QUERY.DESKTOP_LG} {
    margin-right: ${SPACING.MD};
  }
`;

export const SortableItemContainer = styled.div<Partial<{ isDragging: boolean }>>`
  align-items: center;
  background-color: ${COLORS_BACKGROUND.WHITE};
  border: 1px solid ${COLORS_BACKGROUND.OUTLINE_GRAY};
  color: ${COLORS_TEXT.SECONDARY};
  display: flex;
  flex-direction: row;
  padding: ${SPACING.SM};
  position: relative;
  &:hover {
    background-color: ${COLORS_BACKGROUND.GRAY_2};
    ${StyledReorderIcon} {
      color: ${COLORS_TEXT.SECONDARY};
    }
  }

  ${MEDIA_QUERY.DESKTOP_LG} {
    padding: ${SPACING.MD};
  }

  ${({ isDragging }) =>
    isDragging &&
    `
  background-color: ${COLORS_BACKGROUND.GRAY_2};
  box-shadow: '0px 6px 10px rgba(0, 0, 0, 0.2)';
  z-index: ${zIndex.super};`}
`;

export const EntityActionsContainer = styled.div<{ entityEditRemoveName?: string }>`
  flex: 0 0 auto;
  padding-left: ${SPACING.SM};

  ${({ entityEditRemoveName }) => !isPhotoOrVideo(entityEditRemoveName) && `margin-left: auto;`};
`;

export const EntityActionButton = styled.button`
  ${FONT.textSizeCompact(FONT.FONT_SIZE_SMALL)};
  align-items: center;
  background-color: transparent;
  border-radius: 4px;
  border: none;
  color: ${COLORS_LINK.SECONDARY};
  display: flex;
  font-weight: ${FONT.FONT_WEIGHT_SEMI_BOLD};
  padding: ${SPACING.XXS} ${SPACING.XS};

  &:hover {
    background-color: ${COLORS_BACKGROUND.LIGHT_GRAY};
  }
  &:focus {
    outline: 5px auto -webkit-focus-ring-color;
  }

  & + & {
    margin-top: ${SPACING.XS};
  }
`;

export const EntityCaption = styled.div`
  ${FONT.textSizeCompact(FONT.FONT_SIZE_SMALLER)};
  color: ${COLORS_TEXT.SECONDARY};
  flex: 0 0 100%;
  margin-top: ${SPACING.XXS};
`;

export const EntityDetail = styled.div<{ singularItem?: boolean }>`
  flex: 0 1 auto;

  ${({ singularItem }) => singularItem && `margin-top: ${SPACING.XS};`};
`;

export const EntitySecondaryDetail = styled.div`
  flex: 0 0 100%;
`;

export const StyledEditIcon = styled(EditIcon)`
  margin-right: ${SPACING.XS};
`;

export const StyledDeleteIcon = styled(DeleteIcon)`
  margin-right: ${SPACING.XS};
`;

const SharedEntityStyles = css`
  border-radius: 4px;
  border: 1px solid ${COLORS_BACKGROUND.OUTLINE_GRAY};
  height: 120px;
  width: 120px;
`;

export const PhotoEntityPreview = styled.div<{ image: string }>`
  background: ${({ image }) => `url(${image}?w=120&h=120) no-repeat center center`};
  ${SharedEntityStyles};
`;

export const VideoEntityPlaceholder = styled.div`
  background: ${COLORS.GEYSER} url(${VideoSvg}) no-repeat center center;
  ${SharedEntityStyles};
`;

export const EntityCaptionContainer = styled.div`
  margin: 20px 0 20px 0;
  max-width: 600px;
  width: 100%;

  ${MEDIA_QUERY.DESKTOP} {
    margin: 0px 8px 0 20px;
  }
`;

export const PhotoEntityCaption = styled.div`
  font-size: ${FONT.FONT_SIZE_SMALLER};
  margin: ${SPACING.XS} 0 0 0;
  word-wrap: break-word;
  word-break: break-word;
  width: 100%;
`;

export const EntityContainer = styled.div<{
  entityEditRemoveName?: string;
  isMultiPhoto?: boolean;
}>`
  align-items: center;
  display: flex;
  flex: 1 1 auto;

  ${({ entityEditRemoveName }) => isPhotoOrVideo(entityEditRemoveName) && `flex-wrap: wrap;`};

  ${({ isMultiPhoto }) => {
    if (isMultiPhoto) {
      return css`
        ${EntityDetail} {
          order: 1;
        }

        ${EntityActionsContainer} {
          order: 2;
        }

        ${EntityCaptionContainer} {
          order: 3;
        }

        ${MEDIA_QUERY.DESKTOP} {
          flex-wrap: nowrap;

          ${EntityDetail} {
            order: 1;
          }

          ${EntityActionsContainer} {
            order: 3;
          }

          ${EntityCaptionContainer} {
            order: 2;
          }
        }
      `;
    }
    return null;
  }};
`;

export const StyledEditPhotoUploadcare = styled(EditPhotoUploadcare)`
  button {
    padding: ${SPACING.XXS} ${SPACING.XS};
  }
`;
